import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { categoryValidationActionOptions } from "../data";
import { cn } from "@/shared/lib/cn";

type CategoryActionSelectProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

export function CategoryActionSelect({ value, onChange, className }: CategoryActionSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = categoryValidationActionOptions.find((option) => option.value === value);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        title={selected?.label}
        onClick={() => setIsOpen((open) => !open)}
        className="flex h-9 min-w-[12rem] max-w-[15rem] items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 text-left text-xs text-foreground transition-colors hover:bg-accent/20 hover:border-accent focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        <span className="truncate">{selected?.shortLabel ?? selected?.label}</span>
        <ChevronDown size={14} className="shrink-0 text-muted" />
      </button>

      {isOpen && (
        <div className="card-shadow absolute left-0 top-full z-50 mt-1 max-h-64 w-[17rem] overflow-y-auto rounded-xl border border-border bg-card p-1.5">
          {categoryValidationActionOptions.map((option) => {
            const isSelected = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  isSelected
                    ? "bg-primary-soft text-primary"
                    : "text-foreground hover:bg-accent/20",
                )}
              >
                <Check
                  size={14}
                  className={cn("mt-0.5 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                />
                <span className="leading-snug">{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
